/**
 * ReviewAnswerGrid — the shared read-only "review" layout for a completed
 * questionnaire or prescreen, modeled on the WCR Health-History PDF: a responsive
 * multi-column grid of question (muted label) → answer (bold), blanks shown as an
 * en-dash, flagged/failing answers wrapped in a red (exclude/fail) or amber
 * (review) highlight pill, and multi-select answers rendered as checked chips.
 *
 * Both AttemptDetailModal (prescreen eligibility) and FormResponseModal (patient
 * questionnaire) normalize their very different payloads into ReviewItem[] and
 * hand them here, so the two review surfaces look identical.
 */
import { Check } from 'lucide-react';
import { answerHighlight } from './shared';

export interface ReviewItem {
  /** Stable React key (question_key / criterion id). */
  key: string;
  /** The question or criterion prompt — the muted label. */
  label: string;
  /** Formatted answer; any of the empty markers below renders as a muted en-dash. */
  value: string;
  /** Red (exclude / does-not-meet) or amber (review / flag) pill; null = neutral. */
  highlight?: 'exclude' | 'review' | null;
  /** Multi-select: the chosen labels, rendered as a checked list instead of `value`. */
  chips?: string[];
}

interface Props {
  items: ReviewItem[];
  /** PHI masking: redact the VALUES (labels/structure kept). */
  mask?: boolean;
}

// Values that mean "no answer" across both payloads (formatCriterionAnswer →
// "(no answer)", displayAnswer → "—"). Rendered as a muted en-dash, never highlighted.
const EMPTY = new Set(['', '—', '–', '(no answer)']);

/** True when a formatted answer string carries no real value (shared with callers
 *  so the "blank" definition stays in one place). */
export function isBlankAnswer(value: string | null | undefined): boolean {
  return EMPTY.has((value || '').trim());
}

export default function ReviewAnswerGrid({ items, mask = false }: Props) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((it) => {
        const hasChips = Array.isArray(it.chips) && it.chips.length > 0;
        const isEmpty = !hasChips && isBlankAnswer(it.value);
        return (
          <div key={it.key} className="min-w-0">
            <dt className="text-xs leading-snug text-gray-500 dark:text-gray-400">{it.label}</dt>
            <dd className="mt-0.5 text-sm font-semibold">
              {mask ? (
                <span className="text-gray-900 dark:text-gray-100">•••</span>
              ) : hasChips ? (
                <ul className="space-y-1">
                  {it.chips!.map((c, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                      <span className="min-w-0 break-words text-gray-900 dark:text-gray-100">{c}</span>
                    </li>
                  ))}
                </ul>
              ) : isEmpty ? (
                <span className="text-gray-300 dark:text-gray-600">–</span>
              ) : (
                <span className={`whitespace-pre-wrap break-words ${answerHighlight(it.highlight)}`}>
                  {it.value}
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
